import React, { ReactNode, useMemo } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { css } from '@emotion/css';
import { ILegacyBillcheckStatus, LEGACY_BILLCHECK_STATUS } from '../../../../../../../@types/legacyBillCheck';
import { getBillcheckStatus } from '../../../../../../../utils/getBenefitCardStatus.util';
import { useLegacyBillCheck } from '../../../../../../../customHooks/useLegacyBillCheck.hook';
import { TEAL200 } from '../../../../../../../domain/colors';
import { useBillCheckV3 } from '../../hooks/useBillCheckV3.hook';
import { useIntl } from '@/i18n/hooks/useIntl';
import { BILLCHECK_V3_STATUS } from '@/@types/billCheckV3';
import { useStartBillCheckProcess } from '@/apiHooks/billCheckV3.hook';
import { default as Msg } from '@/i18n/components/FormattedMessage';

const styles = {
  headline: css`
    font-weight: 400;
    font-size: 18px;
    line-height: 28px;
  `,
  mainRow: css`
    border-bottom: 2px solid ${TEAL200};
    font-weight: 600;
    display: flex;
    font-size: 14px;
    line-height: 22px;
    justify-content: space-between;

    span {
      padding: 16px;
    }
  `,
  itemRow: css`
    border-bottom: 1px solid #eaeaea;
    font-size: 14px;
    line-height: 22px;
    display: grid;
    grid-template-columns: 1fr 120px;
    padding: 16px;
  `,
  itemInfo: css`
    display: grid;
    grid-template-columns: 100%;
    grid-template-rows: 1fr 1fr;

    @media screen and (min-width: $screenSize-s) {
      display: flex;
      justify-content: space-between;
    }
  `,
  itemDate: css`
    display: flex;
    justify-content: flex-end;
  `,
  bottomRow: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    border-bottom: 1px solid #eaeaea;
    padding: 16px;
  `,
  bottomRowButton: css`
    display: flex;
    justify-content: space-between;
    align-items: center;

    i {
      margin-right: 8px;
    }
  `,
  disclaimer: css`
    padding: 18px 16px 16px 16px;
    font-size: 14px;
    line-height: 22px;
    justify-content: center;
    align-items: flex-start;
    position: relative;

    i {
      position: absolute;
      font-size: 20px;
      left: -8px;
      top: 18px;
      color: var(--color-link-blue);
    }

    p {
      margin-left: 12px;
    }
  `,
};

export const List = () => {
  const { t } = useIntl();
  const { startBillCheck, isBillCheckStarting } = useStartBillCheckProcess();
  const { billCheckState } = useLegacyBillCheck();
  const { billChecks, canStartNewBillCheck, isEntitled } = useBillCheckV3();

  const filteredV3Billchecks = useMemo(() => {
    if (!billChecks) return [];
    return billChecks.filter((billcheck) =>
      [BILLCHECK_V3_STATUS.PROCESSING, BILLCHECK_V3_STATUS.USER_ACTION, BILLCHECK_V3_STATUS.FINISHED].includes(
        billcheck.billCheckStatus,
      ),
    );
  }, [billChecks]);

  const filteredLegacyBillchecks = useMemo(() => {
    if (!billCheckState?.billChecks) return [];
    return billCheckState?.billChecks.filter((billcheck: ILegacyBillcheckStatus) =>
      [LEGACY_BILLCHECK_STATUS.DONE].includes(billcheck?.status),
    );
  }, [billCheckState?.billChecks]);

  const isValidDate = (date: any) => {
    return date instanceof Date && !isNaN(date.getTime());
  };

  if (filteredV3Billchecks.length === 0 && filteredLegacyBillchecks.length === 0) {
    return null;
  }

  return (
    <div className={'margin-top-l'}>
      <h5 className={styles.headline}>
        {t({
          id: 'benefits.billCheckV3.modal.overview.billCheckList.title',
          defaultMessage: 'Meine Prüfungsanträge',
        })}
      </h5>
      <div>
        <div className={styles.mainRow}>
          <span>
            {t({
              id: 'benefits.billCheckV3.modal.overview.billCheckList.orderNumberColumnLabel',
              defaultMessage: 'Order no.',
            })}
          </span>
          <span>
            {t({
              id: 'benefits.billCheckV3.modal.overview.billCheckList.dateColumnLabel',
              defaultMessage: 'Datum',
            })}
          </span>
        </div>
        {filteredV3Billchecks.map((billcheck, i) => (
          <div className={styles.itemRow} key={`${i}-${billcheck.billCheckStatus}`} data-test-ctx={`item-row-${i}`}>
            <div className={styles.itemInfo}>
              <span>{billcheck.ticketId}</span>
              <span>
                {t({
                  id: getBillcheckStatus(billcheck.billCheckStatus!).statusTextTranslationKey!,
                })}
              </span>
            </div>
            <div className={styles.itemDate}>
              {isValidDate(new Date(billcheck.modifiedAt))
                ? format(new Date(billcheck.modifiedAt), 'd. MMMM y', { locale: de })
                : 'Invalid date'}
            </div>
          </div>
        ))}
        {filteredLegacyBillchecks.map((billcheck, i) => (
          <div className={styles.itemRow} key={`${i}-${billcheck.status}`} data-test-ctx={`item-row-${i}`}>
            <div className={styles.itemInfo}>
              <span>{billcheck.minekoTicketId}</span>
              <span>
                {t({
                  id: getBillcheckStatus(billcheck.status!).statusTextTranslationKey!,
                })}
              </span>
            </div>
            <div className={styles.itemDate}>
              {billcheck.updatedAt && isValidDate(new Date(billcheck.updatedAt))
                ? format(new Date(billcheck.updatedAt), 'd. MMMM y', { locale: de })
                : 'Invalid date'}
            </div>
          </div>
        ))}
        {canStartNewBillCheck && (
          <div className={styles.bottomRow}>
            <button
              disabled={isBillCheckStarting}
              className={`${styles.bottomRowButton} button-secondary`}
              onClick={() => startBillCheck(undefined)}
            >
              <i className={'is24-icon-plus'} />
              {t({
                id: 'benefits.billCheckV3.modal.overview.billCheckList.startNewOrderCta',
                defaultMessage: 'Neuen Auftrag starten',
              })}
            </button>
          </div>
        )}
      </div>
      {!canStartNewBillCheck && isEntitled && (
        <div className={styles.disclaimer} data-test-ctx={'disclaimer'}>
          <i className={'is24-icon-info'} />
          <Msg
            id={`benefits.billCheckV3.modal.overview.billCheckList.description`}
            defaultMessage='Als WohnenPlus-Mitglied kannst du einmal im Jahr kostenlos einen Nebenkosten-Check durchführen. Am <b>{nextCheckDate}</b> kannst du deine Nebenkosten erneut prüfen.'
            values={{
              nextCheckDate: '01.01.' + (new Date().getFullYear() + 1),
              b: (children: ReactNode) => <b>{children}</b>,
            }}
          />
        </div>
      )}
    </div>
  );
};
